import IllnessIcon from "modules/common/components/icons/illness.icon.svg?react";
import VacationIcon from "modules/common/components/icons/vacation.icon.svg?react";
import { Constants } from "modules/root/models/constants";
import React from "react";
import { AbsenceValue } from "../models/plan-types";
import WithoutPayIcon from "./without-work.svg?react";
import absenceStyles from "./plan-absence.module.scss";

/** Кнопка отсутствия в окне выбора: те же типы, что и в табеле */
export interface PlanAbsenceOption extends AbsenceValue {
    /** Подпись на кнопке */
    label: string;
    icon: React.ReactNode;
    /** Кнопка занимает отдельную строку — в первую все четыре не влезают */
    wide?: boolean;
}

/**
 * Отпуск, больничный, «без сохранения» и отпуск по беременности — ровно те же
 * типы часов, которыми закрывают день в табеле. В плане дни только рабочие, поэтому проверять
 * `availableForDay` не нужно: типов, запрещённых для выходного, здесь не будет.
 */
export function planAbsenceOptions(): PlanAbsenceOption[] {
    const options: PlanAbsenceOption[] = [
        {
            type: Constants.withoutPayHoursType.name,
            shortName: Constants.withoutPayHoursType.shortName,
            label: "БС",
            icon: <WithoutPayIcon className="figma-icon" />,
        },
        {
            type: Constants.vacationHoursType.name,
            shortName: Constants.vacationHoursType.shortName,
            label: "Отпуск",
            icon: <VacationIcon className="figma-icon" />,
        },
        {
            type: Constants.pregnancyHoursType.name,
            shortName: Constants.pregnancyHoursType.shortName,
            label: "По беременности",
            icon: <VacationIcon className="figma-icon" />,
        },
        // четыре кнопки в одну строку не помещаются — больничный уходит на вторую
        {
            type: Constants.illnesHoursType.name,
            shortName: Constants.illnesHoursType.shortName,
            label: "Больничный",
            icon: <IllnessIcon className="figma-icon" />,
            wide: true,
        },
    ];

    // типы приходят с метаданными приложения; пустой означает, что справочник не доехал
    return options.filter((option) => !!option.type);
}

/**
 * На сетке отпуск по беременности подписывается «ОБ», а не табельной буквой «Р»:
 * одна буква рядом с «О» отпуска читается как опечатка
 */
export const PREGNANCY_SHORT_NAME = "ОБ";

/** Подпись отсутствия на блоке: табельное сокращение, кроме беременности */
export function absenceShortName(absence: AbsenceValue): string {
    return absence.type === Constants.pregnancyHoursType.name ? PREGNANCY_SHORT_NAME : absence.shortName;
}

/** Группа отсутствия — от неё зависит цвет блока на сетке */
export type PlanAbsenceKind = "withoutPay" | "vacation" | "illness" | "pregnancy" | "other";

/**
 * Цвет блока задаётся не инлайном, а классом по группе: палитра живёт в
 * planr/variables.scss (красный — БС, синий — отпуск, зелёный — больничный,
 * жёлтый — по беременности, как в табеле). Прочие типы часов (простой, отгул) приезжают из табеля — им остаётся серый.
 */
export function absenceKind(type: string): PlanAbsenceKind {
    if (type && type === Constants.withoutPayHoursType.name) {
        return "withoutPay";
    }

    if (type && type === Constants.vacationHoursType.name) {
        return "vacation";
    }

    if (type && type === Constants.pregnancyHoursType.name) {
        return "pregnancy";
    }

    if (type && type === Constants.illnesHoursType.name) {
        return "illness";
    }

    return "other";
}

/** Полное название и сокращение отсутствия на сетке */
const ABSENCE_LABELS: Record<PlanAbsenceKind, [string, string]> = {
    withoutPay: ["Без сохранения", "БС"],
    vacation: ["Отпуск", "О"],
    illness: ["Больничный", "Б"],
    pregnancy: ["По беременности", PREGNANCY_SHORT_NAME],
    other: ["", ""],
};

/** Шире этого на блок отсутствия влезает полное название */
const ABSENCE_FULL_LABEL_WIDTH = 90;

/** Уже этого не влезает и сокращение — блок без подписи, тип виден по цвету и подсказке */
const ABSENCE_SHORT_LABEL_WIDTH = 14;

/**
 * Подпись блока отсутствия — одна на блок, одинаково в Табеле План и на «Плановом графике
 * работ»: полное название («Отпуск»), если влезает, иначе сокращение («О»). Прочие типы
 * (простой, отгул) — своим названием и табельным сокращением.
 */
export function absenceLabel(type: string, shortName: string, width: number): string {
    if (width < ABSENCE_SHORT_LABEL_WIDTH) {
        return "";
    }

    const [full, short] = ABSENCE_LABELS[absenceKind(type)];

    return width >= ABSENCE_FULL_LABEL_WIDTH ? full || type : short || shortName || type.slice(0, 1);
}

/** Классы блока отсутствия: общий вид (plan-absence.module.scss) и цвет по группе */
export function absenceClassName(type: string): string {
    return `${absenceStyles.absence} ${absenceStyles[absenceKind(type)]}`;
}
